import { HttpContext, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, filter, forkJoin, from, merge, Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { LicenseType } from '../../enums/license-type';
import { IdsTokenInfo } from '../../models/ids-token-info';
import { LsMpClassResponse } from '../../models/ls-mp-class-response';
import { AuthService } from '../../services/auth.service';
import { CleverService } from '../../services/clever.service';
import {
  ApiVersionActions,
  AuthActions,
  CheckTokenActions,
  CheckUserConfirmationActions,
  LicenseActions,
  ResendActivationEmailActions,
  ResetPasswordActions,
  ScreenSessionActions,
  SetupAccountActions,
  SetupUserSchoolActions,
  SignupAccountActions,
  UserClassesAction,
  UserDistrictActions,
  UserLicenseActions,
  UserSchoolsActions,
  UserSessionActions
} from './auth.actions';
import * as AuthSelectors from './auth.selectors';
import { ENV_CONFIG, EnvironmentConfig } from '../../../environment.config';
import { MpAuthInfo } from '../../models/mp-auth-info';
import { StudioSchoolResponse } from '../../models/studio-school-response';
import { withNgHttpCachingContext } from '../../ng-http-caching/ng-http-caching.service';
import { NycidpService } from '../../services/nycidp.service';
import { SessionStorageUtils } from '../../../shared/utils/session-storage.utils';
import { ClassesService } from '../../services/classes.service';
import { SchoolsService } from '../../services/schools.service';
import { StringUtils } from '../../../shared/utils/string.utils';
import { EnvironmentService } from '../../services/environment.service';
import { AcademicYearService } from '../../services/academic-year.service';
import { SessionsService } from '../../services/sessions.service';
import { UserType } from '../../enums/user-type.enum';
import { SsoProvider } from '../../../shared/enums/sso-provider.enum';
import { ClassResponse } from '../../models/class-response';
import { EndSession } from '../../models/end-session';
import { ScreenSessionResponse } from '../../models/screen-session-response';
import { StartSession } from '../../models/start-session';
import { StudentResponse } from '../../models/student-response';
import { TeacherResponse } from '../../models/teacher-response';
import { BrowserDetectionUtils } from '../../../shared/utils/browser-detection-utils';
import { DeviceService } from '../../services/device.service';
import { SchoolAcademicYearResponse } from '../../models/school-academic-year-response';
import { StudentService } from '../../services/student.service';
import { ScreenSessionService } from '../../services/screen-session.service';
import { TeacherService } from '../../services/teacher.service';
import { AdminType } from '../../enums/admin-type.enum';
import {
  APPLICATION_BUILD_TYPE,
  APPLICATION_PLATFORM,
  AUTHENTICATION_METHOD,
  CLASS_ID,
  MINDPLAY_EMPLOYEE_ROLES,
  SCHOOL_ID,
  USER_SESSION,
} from '../../constants/constants';
import { DateUtils } from '../../../shared/utils/date.utils';
import { TokenService } from '../../services/token.service';
import { ServiceWorkerUpdateService } from '../../services/service-worker-update.service';
import { MathUtils } from '../../../shared/utils/math.utils';
import { LicenseService } from '../../services/license.service';
import { isNullOrUndefined } from '../../../shared/utils/helper.utils';
import { setUser } from '@sentry/angular';
import { MindplayUserRole } from '../../enums/mindplay-user-role';
import { LsAuthResponse } from '../../models/ls-auth-response';
import { TrialTeacherByClassActivationBindingModel } from '../../models/trial-teacher-by-class-activation-binding-model';
import { TrialTeacherBySchoolActivationBindingModel } from '../../models/trial-teacher-by-school-activation-binding-model';
import { DeviceTypeUtils } from '../../../shared/utils/device-type-utils';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private authService = inject(AuthService);
  private tokenService = inject(TokenService);
  private cleverService = inject(CleverService);
  private classesService = inject(ClassesService);
  private nycidpService = inject(NycidpService);
  private schoolsService = inject(SchoolsService);
  private studentService = inject(StudentService);
  private teacherService = inject(TeacherService);
  private sessionsService = inject(SessionsService);
  private deviceService = inject(DeviceService);
  private environmentConfig = inject<EnvironmentConfig>(ENV_CONFIG);
  private screenSessionService = inject(ScreenSessionService);
  private academicYearService = inject(AcademicYearService);
  private environmentService = inject(EnvironmentService);
  private serviceWorkerUpdate = inject(ServiceWorkerUpdateService);
  private licenseService = inject(LicenseService);

  authenticate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticate),
      switchMap(action => {
        let context: HttpContext;
        // Only pass caching header if it has customerId, indicates that this is student
        if (action.customerId) {
          context = withNgHttpCachingContext({
            isCacheable: () => {
              return true;
            },
            getKey: (req: HttpRequest<unknown>) => {
              // Custom unique key to store the data
              return (
                action.username +
                '@' +
                window.btoa(action.password) +
                '@' +
                req.method +
                '@' +
                req.urlWithParams
              );
            },
          });
        }
        return this.authService
          .authenticate(action.customerId, action.username, action.password, context)
          .pipe(
            map(res => {
              return AuthActions.authenticateLSMP({ token: res.access_token });
            }),
            catchError(err => of(AuthActions.authenticateFailure({ error: err })))
          );
      })
    );
  });

  onlineAuthenticate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.onlineAuthenticate),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectMpCustomerId),
        this.store.select(AuthSelectors.selectMpUsername),
      ]),
      map(([, customerId, username]) => {
        const mpToken = this.tokenService.getAuthTokenByKey('mpToken');
        const accessTokenInfo = StringUtils.parseJwt(mpToken);
        return AuthActions.authenticate({
          customerId,
          username,
          password: accessTokenInfo['password'],
        });
      })
    );
  });

  authenticateLSMP$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateLSMP),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectEmployeeLoggedInAs)),
      switchMap(([action, employeeLoggedInAs]) => {
        const parsedToken = StringUtils.parseJwt(action.token);
        const idsTokenInfo: IdsTokenInfo = {
          userId: parsedToken['userId'],
          username: parsedToken['username'],
          role: parsedToken['role'],
          source: parsedToken['source'],
          password: parsedToken['password'],
          licensedProducts: parsedToken['licensedProducts']
            ? JSON.parse(parsedToken['licensedProducts'])
            : [],
          customerId: parsedToken['customerId'],
          userType: this.getUserTypeByRole(parsedToken['role'], !!employeeLoggedInAs),
          adminType: this.getAdminTypeByRole(parsedToken['role'], employeeLoggedInAs?.adminType),
          isProgramManager: parsedToken['role'] === MindplayUserRole.ProgramManager,
        };

        // Do not check license info for super admin
        if (idsTokenInfo.userType === UserType.MPSuperAdmin) {
          return forkJoin([
            this.authService.authenticateLS({ idToken: action.token }),
            this.authService.authenticateMP(action.token, DateUtils.new().utcOffset() / 60).pipe(
              map(res => {
                const parsedToken = StringUtils.parseJwt(res.access_token);
                return {
                  ...res,
                  customerId: +parsedToken['customerId'],
                  customerName: parsedToken['customerName'],
                  userFirstName: parsedToken['userFirstName'],
                  userLastName: parsedToken['userLastName'],
                  userId: +parsedToken['userId'],
                  email: parsedToken['email'],
                  classIds: parsedToken['classes'] ? JSON.parse(parsedToken['classes']) : [],
                  closeTabOnLogout: parsedToken['closeTab'] && parsedToken['closeTab'] === 'True',
                } as MpAuthInfo;
              })
            ),
          ]).pipe(
            switchMap(([lsAuthResponse, mpAuthResponse]) => {
              setUser({
                customerId: idsTokenInfo.customerId,
                id: mpAuthResponse.userId,
                username:
                  mpAuthResponse.email && mpAuthResponse.email !== 'none'
                    ? mpAuthResponse.email
                    : idsTokenInfo.username,
              });

              // Store token
              this.tokenService.setAuthToken({
                idToken: action.token,
                mpToken: mpAuthResponse.access_token,
                lsToken: lsAuthResponse?.access_token,
                lsShardKey: lsAuthResponse?.ShardKey,
              });

              return [
                AuthActions.authenticateSuccess({
                  idsTokenInfo,
                  lsAuthResponse,
                  mpAuthResponse,
                }),
                // Mark the auth workflow process completed for PLS
                AuthActions.authFlowCompleted(),
              ];
            })
          );
          // Check for (valid) studio licenses
        } else if (
          [
            LicenseType.StudioUSA,
            LicenseType.StudioInternational,
            LicenseType.DyslexiaScreener,
            LicenseType.Signals,
          ].some(license => idsTokenInfo.licensedProducts?.includes(license))
        ) {
          // Proceed based on user type
          if (
            idsTokenInfo.userType === UserType.Student ||
            idsTokenInfo.userType === UserType.Teacher
          ) {
            let context: HttpContext;
            // Only pass caching header for student
            if (idsTokenInfo.userType === UserType.Student) {
              context = withNgHttpCachingContext({
                isCacheable: () => {
                  return true;
                },
                getKey: (req: HttpRequest<unknown>) => {
                  // Custom unique key to store the data
                  return idsTokenInfo.username + '@' + req.method + '@' + req.urlWithParams;
                },
              });
            }

            let authObservables: Observable<[LsAuthResponse, MpAuthInfo]>;

            if (action.lsToken && action.mpToken) {
              authObservables = forkJoin([
                this.authService.authenticateLS({ idToken: action.token, preservePreviousToken: true, context }),
                of(action.mpToken).pipe(
                  map(token => MpAuthInfo.getMpAuthInfo(token, employeeLoggedInAs?.customerId))
                ),
              ]);
            } else {
              authObservables = forkJoin([
                // User username and password received from JWT token for LS authenticate if user role is student or teacher and is coming from MP temporarily
                this.authService.authenticateLS({ idToken: action.token, context }),
                this.authService
                  .authenticateMP(action.token, DateUtils.new().utcOffset() / 60, context)
                  .pipe(
                    map(res =>
                      MpAuthInfo.getMpAuthInfo(res.access_token, employeeLoggedInAs?.customerId)
                    )
                  ),
              ]);
            }

            return authObservables.pipe(
              switchMap(([lsAuthResponse, mpAuthResponse]) => {
                setUser({
                  customerId: idsTokenInfo.customerId,
                  id: mpAuthResponse.userId,
                  username:
                    mpAuthResponse.email && mpAuthResponse.email !== 'none'
                      ? mpAuthResponse.email
                      : idsTokenInfo.username,
                });

                // check if this is a student trial account and if trial is expired then stop login
                if (
                  idsTokenInfo.userType === UserType.Student &&
                  mpAuthResponse.trialDaysRemaining !== null &&
                  mpAuthResponse.trialDaysRemaining <= 0
                ) {
                  throw new Error('No Licenses Available for this User.');
                }

                // @TODO: Save all tokens into storage, either local or IDB for rehydration to avoid authenticate calls
                // Store Token
                this.tokenService.setAuthToken({
                  idToken: action.token,
                  mpToken: mpAuthResponse.access_token,
                  lsToken: lsAuthResponse?.access_token,
                  lsShardKey: lsAuthResponse?.ShardKey,
                });

                // Google Tag Manager Reporting - START
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'login',
                  userId: idsTokenInfo.userId,
                  accountId: mpAuthResponse.customerId,
                  role: idsTokenInfo.role,
                  email: mpAuthResponse.email,
                  customer: mpAuthResponse.customerName,
                  name: mpAuthResponse.userFirstName,
                });
                // Google Tag Manager Reporting - END

                // Here in the future when we allow more user types like district admins or partner admins
                // who doesn't have classes then we can skip the loadUserClasses Action and call authenticateProcessCompleted() to proceed
                if (idsTokenInfo.userType === UserType.Student) {
                  return [
                    AuthActions.authenticateSuccess({
                      idsTokenInfo,
                      lsAuthResponse,
                      mpAuthResponse,
                    }),
                    SetupUserSchoolActions.setupUserSchool({
                      schoolId: lsAuthResponse?.schoolId,
                      isAllSelected: false,
                    }),
                  ];
                } else if (
                  idsTokenInfo.adminType === AdminType.Unknown ||
                  idsTokenInfo.adminType === AdminType.SchoolAdmin ||
                  idsTokenInfo.adminType === AdminType.DistrictAdmin
                ) {
                  const actions: Action[] = [
                    AuthActions.authenticateSuccess({
                      idsTokenInfo,
                      lsAuthResponse,
                      mpAuthResponse,
                    }),
                    UserSchoolsActions.loadUserSchools(),
                  ];

                  if (idsTokenInfo.adminType === AdminType.DistrictAdmin) {
                    actions.push(UserDistrictActions.loadDistrictAcademicYears());
                  }

                  return actions;
                }
              })
            );
          } else {
            return of(
              AuthActions.authenticateFailure({
                error: new HttpErrorResponse({ error: 'No Login Flow defined for this User.' }),
              })
            );
          }
          // If source is mindplay and user only has one of MP licenses then redirect them to MP
        } else if (
          idsTokenInfo.source === 'Mindplay' &&
          (idsTokenInfo.licensedProducts?.includes(LicenseType.MVRC) ||
            idsTokenInfo.licensedProducts?.includes(LicenseType.FLRTOnly) ||
            idsTokenInfo.licensedProducts?.includes(LicenseType.MindPlayLiteracy) ||
            idsTokenInfo.licensedProducts?.includes(LicenseType.Mathematics) ||
            idsTokenInfo.licensedProducts?.includes(LicenseType.MindPlayLiteracyPlus))
        ) {
          // If MP user doesn't have studio license and have MVRC or UniversalScreener license then redirect them to MP
          if (idsTokenInfo.userType === UserType.Student) {
            window.location.href = `${this.environmentConfig.mpAccountUrl}?it=${action.token}`;
          } else {
            window.location.href = `${this.environmentConfig.mpManagerUrl}?it=${action.token}`;
          }
          return of(
            AuthActions.authenticateSuccess({
              idsTokenInfo,
              lsAuthResponse: null,
              mpAuthResponse: null,
            })
          );
          // If source is mindplay and user doesn't have license then stop workflow
        } else if (idsTokenInfo.source === 'Mindplay' && !idsTokenInfo.licensedProducts?.length) {
          return of(
            AuthActions.authenticateFailure({
              error: new HttpErrorResponse({ error: 'No Licenses Available for this User.' }),
            })
          );
          // If source is LS and user doesn't have studio license then redirect them to LS
        } else if (
          idsTokenInfo.source === 'LightSail' &&
          !idsTokenInfo.licensedProducts?.includes(LicenseType.StudioUSA) &&
          !idsTokenInfo.licensedProducts?.includes(LicenseType.StudioInternational)
        ) {
          // If LS user doesn't have studio license then redirect them to LS v10
          window.location.href = `${this.environmentConfig.lsV10Url}?it=${action.token}`;
          return of(
            AuthActions.authenticateSuccess({
              idsTokenInfo,
              lsAuthResponse: null,
              mpAuthResponse: null,
            })
          );
        } else {
          return of(
            AuthActions.authenticateFailure({
              error: new HttpErrorResponse({
                error: 'This Licenses type is not supported in app.',
              }),
            })
          );
        }
      }),
      catchError((err, caught) =>
        merge(caught, of(AuthActions.authenticateFailure({ error: err })))
      )
    );
  });

  authenticateSSO$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateSSO),
      switchMap(action => {
        switch (action.ssoProvider) {
          case SsoProvider.Google:
            return of({ token: action.token, ssoProvider: action.ssoProvider });
          case SsoProvider.Clever:
            if (action.token) {
              return this.cleverService
                .exchangeToken(action.token)
                .pipe(map(token => ({ token: token, ssoProvider: action.ssoProvider })));
            } else {
              return this.cleverService
                .authenticate()
                .pipe(map(token => ({ token: token, ssoProvider: action.ssoProvider })));
            }
          case SsoProvider.Nyc:
            return from(this.nycidpService.exchangeToken(action.token)).pipe(
              map(token => ({ token: token, ssoProvider: action.ssoProvider }))
            );
          default:
            return of({ token: null, ssoProvider: null });
        }
      }),
      switchMap(({ token, ssoProvider }) => {
        return this.authService.authenticateViaSSO(token, ssoProvider).pipe(
          map(res => {
            return AuthActions.authenticateLSMP({ token: res.access_token });
          })
        );
      }),
      catchError((err, caught) =>
        merge(caught, of(AuthActions.authenticateFailure({ error: err })))
      )
    );
  });

  authenticateViaAccessToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateViaAccessToken),
      switchMap(({ token }) => {
        const parsedToken = StringUtils.parseJwt(token);
        // When user is redirected from clever or classlink then user first goes to account.mindplay.com and then redirected to studio
        // and the identity token in the url will contain authenticationMethod which will be used to auto close the tab on logout
        SessionStorageUtils.save(AUTHENTICATION_METHOD, parsedToken[AUTHENTICATION_METHOD]);

        // If the app is opened in in-app webview like classlink ios app then store the credentials and auth method to
        if (
          parsedToken[AUTHENTICATION_METHOD] === 'classLink' &&
          DeviceTypeUtils.isiPadInAppWebView()
        ) {
          window.localStorage.setItem(AUTHENTICATION_METHOD, parsedToken[AUTHENTICATION_METHOD]);
          window.localStorage.setItem(
            'c',
            window.btoa(
              `${parsedToken['customerId']}|${parsedToken['username']}|${parsedToken['password']}`
            )
          );
        }
        if (parsedToken['customerId'] && parsedToken['username'] && parsedToken['password']) {
          return of(AuthActions.authenticate({
              customerId: parsedToken['customerId'],
              username: parsedToken['username'],
              password: parsedToken['password'],
            }))
        } else {
          return of(AuthActions.authenticateLSMP({ token }));
        }
      }),
      catchError((err, caught) => {
        console.error('Failed to parse token: ', err);
        return merge(caught, EMPTY);
      })
    )
  });

  authenticateViaTokenExchange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.authenticateViaTokenExchange),
      switchMap(({ exchangeId, exchangeProvider }) => {
        return this.authService.exchangeMPToken(exchangeId, exchangeProvider).pipe(
          map(({access_token}) => AuthActions.authenticateViaAccessToken({token: access_token})),
          catchError(err => {
            console.error('Error exchanging token: ', err);
            return EMPTY;
          })
        );
      })
    );
  });

  logoutMPManagerUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logoutMPManagerUser),
      concatLatestFrom(() => [this.store.select(AuthSelectors.selectUserType)]),
      // Students don't have access to Manager, so don't make this call
      filter(([, userType]) => userType !== UserType.Student),
      switchMap(() => {
        const mpToken = this.tokenService.getAuthTokenByKey('mpToken');
        return this.authService
          .logoutMPManagerUser(mpToken)
          .pipe(map(() => AuthActions.logoutMPManagerUserSuccess()));
      }),
      catchError((err, caught) =>
        merge(caught, of(AuthActions.logoutMPManagerUserFailure({ error: err })))
      )
    );
  });

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => {
        setUser(null);

        return UserSessionActions.endUserSession({ logout: true });
      })
    );
  });

  clearCacheAndStore$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.clearCacheAndStore),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectCloseTabOnLogout)),
      map(([, closeTabOnLogout]) => {
        // This clears cache
        this.clearCacheAndCheckUpdate(closeTabOnLogout);
        // This clears ngrx store in meta reducer
        return AuthActions.logoutSuccess();
      })
    );
  });

  loadUserSchools$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserSchoolsActions.loadUserSchools),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectDefaultSchoolId),
        this.store.select(AuthSelectors.selectDistrictAdminDistrictId),
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectEmployeeLoggedInAs),
        this.store.select(AuthSelectors.selectAdminType),
      ]),
      switchMap(
        ([, defaultSchoolId, districtAdminDistrictId, userId, employeeLoggedInAs, adminType]) =>
          this.schoolsService
            .getSchoolsByUserId(userId, true)
            .pipe(
              map(schools => {
                if (employeeLoggedInAs?.districtId) {
                  return schools.filter(sch => {
                    let baseCondition = +sch.mpDistrictId === +employeeLoggedInAs.districtId;

                    if (employeeLoggedInAs.schoolIds?.length > 0) {
                      baseCondition =
                        baseCondition && employeeLoggedInAs.schoolIds.includes(+sch.mpSchoolId);
                    }

                    return baseCondition;
                  });
                }

                return schools;
              }),
              switchMap(schools =>
                forkJoin({
                  schools: of(schools),
                  academicYears: this.academicYearService.getSchoolsAcademicYears(
                    schools.map(sch => sch.schoolId)
                  ),
                })
              )
            )
            .pipe(
              switchMap(({ schools, academicYears }) => {
                const now = new Date();
                const currentAcademicYears: SchoolAcademicYearResponse[] = academicYears.filter(
                  ay => new Date(ay.startDate) <= now && new Date(ay.endDate) >= now
                );
                const academicYearMap = currentAcademicYears.reduce(
                  (acc, item) => ({ ...acc, [item.schoolId]: item }),
                  {}
                );

                // Populate Mp schoolId in school response
                let studioSchools: StudioSchoolResponse[] = schools.map(sch => ({
                  ...sch,
                  // Force converting mpSchool and mpDistrict id to number since now they are coming directly from LS
                  mpSchoolId: +sch.mpSchoolId,
                  mpDistrictId: +sch.mpDistrictId,
                  academicYear: academicYearMap[sch.schoolId],
                }));

                // Remove schools that are not from current user's district for district admin
                // There a bug in the BE which would assign a different districtId to newly created schools
                if (adminType === AdminType.DistrictAdmin && districtAdminDistrictId) {
                  studioSchools = studioSchools.filter(
                    sch => sch.districtId === districtAdminDistrictId
                  );
                }

                // Only exclude default school if user has more than one school
                const studioSchoolsExcludingDefault =
                  studioSchools.length > 1
                    ? studioSchools.filter(
                        sch => sch.userGroupOrganizationName !== 'Default School'
                      )
                    : studioSchools;

                const schoolsWithClasses = studioSchoolsExcludingDefault.filter(
                  sch => sch.hasActiveClasses
                );

                // Check if there is already a schoolId in local storage for that user
                // If there is then in that case preselect that school otherwise select first school
                const selectedSchoolId = window.localStorage.getItem(`${userId}-${SCHOOL_ID}`);

                const selectedSchool = selectedSchoolId
                  ? schoolsWithClasses.find(sch => sch.schoolId === selectedSchoolId)
                  : schoolsWithClasses.find(sch => sch.schoolId === defaultSchoolId);

                const schoolsWithoutSchoolId = studioSchoolsExcludingDefault
                  .filter(
                    school =>
                      isNullOrUndefined(school.mpSchoolId) || isNullOrUndefined(school.schoolId)
                  )
                  .map(
                    school =>
                      `${school.userGroupOrganizationName}. LS School ID: ${school.schoolId}. MP School ID: ${school.mpSchoolId}`
                  )
                  .join(' | ');

                if (schoolsWithoutSchoolId?.length) {
                  console.error(
                    `LS District ID: ${districtAdminDistrictId}. No associated IDs found for the following schools: ${schoolsWithoutSchoolId}`,
                    schoolsWithoutSchoolId
                  );
                }

                const school =
                  selectedSchool ?? schoolsWithClasses?.[0] ?? studioSchoolsExcludingDefault?.[0];

                if (!school) {
                  throw new Error('No Schools found.');
                }

                const districtId = school.mpDistrictId ?? employeeLoggedInAs?.districtId;

                return [
                  UserSchoolsActions.loadUserSchoolsSuccess({
                    schools: studioSchoolsExcludingDefault,
                  }),
                  SetupUserSchoolActions.setupUserSchool({
                    schoolId: school.schoolId,
                    isAllSelected: adminType === AdminType.DistrictAdmin,
                  }),
                  LicenseActions.loadLicensesByDistrictId({
                    districtId,
                  }),
                ];
              }),
              catchError(error => of(UserSchoolsActions.loadUserSchoolsFailure({ error })))
            )
      )
    );
  });

  setupUserSchool$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetupUserSchoolActions.setupUserSchool),
      concatLatestFrom(action => [
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectUserType),
        this.store.select(AuthSelectors.selectAdminType),
        this.store.select(AuthSelectors.selectMpUsername),
        this.store.select(AuthSelectors.selectStudentId),
        this.store.select(AuthSelectors.selectTeacherId),
        this.store.select(AuthSelectors.selectUserSchoolForSchoolId(action?.schoolId)),
      ]),
      switchMap(
        ([
          action,
          userId,
          userType,
          adminType,
          mpUsername,
          studentId,
          teacherId,
          selectedSchool,
        ]) => {
          // Storing schoolId in local storage so it can be pre-set during login in case of browser refresh/re-login
          window.localStorage.setItem(`${userId}-${SCHOOL_ID}`, action.schoolId);

          let context: HttpContext;
          // Only pass caching header for student
          if (userType === UserType.Student) {
            context = withNgHttpCachingContext({
              isCacheable: () => {
                return true;
              },
              getKey: (req: HttpRequest<unknown>) => {
                // Custom unique key to store the data
                return mpUsername + '@' + req.method + '@' + req.urlWithParams;
              },
            });
          }

          return forkJoin([
            // mpSchoolId is not available for student, in the future, that can be added to mp auth token for student if needed
            selectedSchool
              ? of(selectedSchool)
              : this.schoolsService
                  .getSchool(action.schoolId, context)
                  .pipe(map(res => ({ ...res, mpSchoolId: null, academicYear: null }))),
            this.schoolsService.getSchoolMetaData(
              action.schoolId,
              adminType === AdminType.DistrictAdmin,
              context
            ),
          ]).pipe(
            switchMap(([school, metaData]) => {
              const id = userType === UserType.Student ? studentId : teacherId;

              return [
                SetupUserSchoolActions.setupUserSchoolSuccess({
                  school,
                  metaData,
                  isAllSelected: action.isAllSelected,
                }),
                UserClassesAction.loadUserClasses({
                  id,
                  userType,
                  adminType,
                  schoolId: action.schoolId,
                  mpSchoolId: school.mpSchoolId,
                  preSelectMPClassId: action.preSelectMPClassId,
                }),
                ...(school.mpSchoolId
                  ? [
                      LicenseActions.loadLicensesBySchoolId({
                        schoolId: school.mpSchoolId,
                      }),
                    ]
                  : []),
              ];
            }),
            catchError(error => of(SetupUserSchoolActions.setupUserSchoolFailure({ error })))
          );
        }
      )
    );
  });

  loadUserClasses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserClassesAction.loadUserClasses),
      concatLatestFrom(() => [this.store.select(AuthSelectors.selectMpUsername)]),
      switchMap(([action, mpUsername]) => {
        let observable: Observable<ClassResponse[]>;
        if (action.userType === UserType.Student) {
          const context = withNgHttpCachingContext({
            isCacheable: () => {
              return true;
            },
            getKey: (req: HttpRequest<unknown>) => {
              // Custom unique key to store the data
              return mpUsername + '@' + req.method + '@' + req.urlWithParams;
            },
          });

          observable = this.classesService.getClassesByStudent(action.id, context);
        } else {
          observable = this.schoolsService.getClassesInSchool(
            action.schoolId,
            action.adminType === AdminType.DistrictAdmin
          );
        }

        return observable.pipe(
          map(classes => {
            if (!classes?.length) {
              throw new HttpErrorResponse({ error: 'No active classes in selected school.' });
            }

            return classes
              .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
              .map<LsMpClassResponse>((lsCls: ClassResponse) => {
                return {
                  ...lsCls,
                  mpIdAtSource: +lsCls.rosterProviderName,
                };
              });
          }),
          switchMap(classes => {
            const classIds = classes.map(cls => cls.classId);
            return forkJoin({
              classes: of(classes),
              adminType: of(action.adminType),
              preSelectMPClassId: of(action.preSelectMPClassId),
              classStudentsMap:
                action.userType === UserType.Teacher
                  ? this.studentService
                      .getStudentsBySchoolAndClasses(action.schoolId, classIds)
                      .pipe(
                        map(students => {
                          let classStudentsMap: Record<string, StudentResponse[]> = {};
                          students.map(({ classIds, ...student }) => {
                            student.schoolId = action.schoolId;
                            student.fullName = `${student.firstName} ${student.lastName}`;
                            classIds.map(classId => {
                              classStudentsMap = {
                                ...classStudentsMap,
                                [classId]: [...(classStudentsMap[classId] || []), student],
                              };
                            });
                          });

                          // Sort the students within each class by fullName
                          Object.keys(classStudentsMap).forEach(classId => {
                            classStudentsMap[classId].sort((a, b) =>
                              (a.fullName ?? '')
                                .toLowerCase()
                                .localeCompare((b.fullName ?? '').toLowerCase())
                            );
                          });

                          return classStudentsMap;
                        })
                      )
                  : of(null),
              classTeachersMap:
                action.userType === UserType.Teacher
                  ? this.teacherService
                      .getTeachersBySchoolAndClasses(action.schoolId, classIds)
                      .pipe(
                        map(teachers => {
                          let classTeachersMap: Record<string, TeacherResponse[]> = {};
                          teachers.map(({ classIds, ...teacher }) => {
                            teacher.fullName = `${teacher.firstName} ${teacher.lastName}`;
                            classIds.map(classId => {
                              classTeachersMap = {
                                ...classTeachersMap,
                                [classId]: [...(classTeachersMap[classId] || []), teacher],
                              };
                            });
                          });

                          // Sort the Teachers within each class by fullName
                          Object.keys(classTeachersMap).forEach(classId => {
                            classTeachersMap[classId].sort((a, b) =>
                              (a.fullName ?? '')
                                .toLowerCase()
                                .localeCompare((b.fullName ?? '').toLowerCase())
                            );
                          });

                          return classTeachersMap;
                        })
                      )
                  : of(null),
            });
          })
        );
      }),
      switchMap(
        (userClassesData: {
          classes: LsMpClassResponse[];
          adminType: AdminType;
          preSelectMPClassId: number;
          classStudentsMap: Record<string, StudentResponse[]> | null;
          classTeachersMap: Record<string, TeacherResponse[]> | null;
        }) => {
          // Check if there is already a classId in session storage or passed from action
          // If there is then its a reload procedure, in that case preselect that class and mark the flow as complete
          const selectedClassId = SessionStorageUtils.get(CLASS_ID);
          const selectedMpClassId = userClassesData.preSelectMPClassId;

          // Class lookup
          let selectedLSClass: LsMpClassResponse;
          let selectedMPClass: LsMpClassResponse;

          if (selectedMpClassId) {
            selectedMPClass = userClassesData.classes.find(
              cls => cls.mpIdAtSource === selectedMpClassId
            );
          }
          if (selectedClassId) {
            selectedLSClass = userClassesData.classes.find(cls => cls.classId === selectedClassId);
          }

          // selectedMpClass takes preference
          const selectedClass = selectedMPClass ?? selectedLSClass ?? userClassesData.classes[0];

          // Pre-select the class either from MP classId, LS classId or take the first class from classes
          // This is will remove the need for login component to select a class
          return [
            UserClassesAction.loadUserClassesSuccess({ userClassesData }),
            UserClassesAction.selectUserClass({
              selectedClass,
              isAllSelected:
                !selectedMpClassId &&
                (userClassesData.adminType === AdminType.DistrictAdmin ||
                  userClassesData.adminType === AdminType.SchoolAdmin),
            }),
            AuthActions.authFlowCompleted(),
          ];
        }
      ),
      catchError((error, caught) =>
        merge(caught, of(UserClassesAction.loadUserClassesFailure({ error })))
      )
    );
  });

  selectUserClass$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserClassesAction.selectUserClass),
      switchMap(action => {
        // Storing classId in session storage so it can be recovered in case of browser refresh
        SessionStorageUtils.save(CLASS_ID, action.selectedClass.classId);

        return [
          // End Previous Login Session
          UserSessionActions.endUserSession({ logout: false }),
          // Start New Login Session
          UserSessionActions.startUserSession({ userGroupId: action.selectedClass.userGroupId }),
        ];
      })
    );
  });

  startUserSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserSessionActions.startUserSession),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectUTCOffset),
        this.store.select(AuthSelectors.selectIsMpSalesRepOrMpEmployee),
      ]),
      filter(([, , , isMpSalesRepOrMpEmployee]) => !isMpSalesRepOrMpEmployee),
      switchMap(([action, userId, utcOffset]) => {
        const now = DateUtils.new().utcOffset(utcOffset, false); //get the utcOffset fixed date
        const startSession: StartSession = {
          isOffline: false,
          offlineSessionId: StringUtils.uuidv4(),
          userId: userId,
          userGroupId: action.userGroupId,
          startTime: now.toISOString(),
          deviceType: this.deviceService.getDeviceType(),
          deviceVersion: JSON.stringify({
            platform: BrowserDetectionUtils.getApplicationPlatform(),
            browserVersion: BrowserDetectionUtils.getBrowserVersion(),
          }),
          applicationPlatform: APPLICATION_PLATFORM,
          applicationBuildType: APPLICATION_BUILD_TYPE,
          lightSailVersion: this.environmentConfig.version,
          isOutsideSchoolHours: false, //TODO: remove hardcoded value when outside school hour feature implement
          isDaylightSaving: false, //TODO: remove hardcoded value when outside school hour feature implement
          utcOffset: utcOffset ? utcOffset : 0,
          localTime: now.format('HH:mm:ss'),
          environment: this.environmentConfig.environment,
        };
        return this.sessionsService.postStartSession(userId, startSession).pipe(
          switchMap(res => {
            return [
              UserSessionActions.startUserSessionSuccess({ sessionId: res.objectId }),
              UserSessionActions.userSessionPolling(),
            ];
          }),
          catchError(error => of(UserSessionActions.startUserSessionFailure({ error })))
        );
      })
    );
  });

  userSessionPolling$ = createEffect(() => {
    // One time exception, had to cycle effect for polling
    // eslint-disable-next-line @ngrx/avoid-cyclic-effects
    return this.actions$.pipe(
      ofType(UserSessionActions.userSessionPolling),
      // Polling after 120 seconds
      // Wrapping it inside switch map will make sure that there's only one polling action in waiting
      // This will cancel any previous polling action waiting under delay if next  polling action triggers
      switchMap(() => of({}).pipe(delay(120000))),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectUTCOffset),
        this.store.select(AuthSelectors.selectUserSessionId),
      ]),
      filter(([, userId, , sessionId]) => !!userId && !!sessionId),
      switchMap(([, userId, utcOffset, sessionId]) => {
        const now = DateUtils.new().utcOffset(utcOffset || 0, false); //get the utcOffset fixed date
        const endTime = now.toISOString();

        const endSession: EndSession = {
          userId,
          endTime: endTime,
          sessionId: sessionId,
          offlineSessionId: sessionId,
          mustLogout: false,
          isOffline: false,
        };

        return this.sessionsService.postEndSession(endSession.userId, endSession).pipe(
          map(() => UserSessionActions.userSessionPolling()),
          catchError(() => of(UserSessionActions.userSessionPolling()))
        );
      })
    );
  });

  endUserSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserSessionActions.endUserSession),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectUTCOffset),
        this.store.select(AuthSelectors.selectUserSessionId),
      ]),
      switchMap(([action, userId, utcOffset, sessionId]) => {
        const previousSession: EndSession = JSON.parse(window.localStorage.getItem(USER_SESSION));

        let endSession: EndSession;
        if (sessionId) {
          const now = DateUtils.new().utcOffset(utcOffset || 0, false); //get the utcOffset fixed date
          const endTime = now.toISOString();

          endSession = {
            userId,
            endTime: endTime,
            sessionId: sessionId,
            offlineSessionId: sessionId,
            mustLogout: action.logout,
            isOffline: false,
          };

          // Saving the session into local storage so if the session is failed to save on API, we will retry it on next login
          window.localStorage.setItem(USER_SESSION, JSON.stringify(endSession));
        } else if (previousSession?.sessionId) {
          endSession = { ...previousSession, mustLogout: false };
        } else {
          endSession = null;
        }

        const observable =
          endSession && endSession.userId
            ? this.sessionsService.postEndSession(endSession.userId, endSession)
            : of({});
        return observable.pipe(
          switchMap(() => {
            // Clear session object from local storage if its same
            const session: EndSession = JSON.parse(window.localStorage.getItem(USER_SESSION));

            if (endSession && endSession?.sessionId === session?.sessionId) {
              window.localStorage.removeItem(USER_SESSION);
            }

            const actions: Action[] = [UserSessionActions.endUserSessionSuccess()];

            if (action.logout) {
              actions.push(AuthActions.clearCacheAndStore());
            }

            return actions;
          }),
          catchError(error => {
            const actions: Action[] = [UserSessionActions.endUserSessionFailure({ error })];
            if (action.logout) {
              actions.push(AuthActions.clearCacheAndStore());
            }
            return actions;
          })
        );
      })
    );
  });

  logScreenSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ScreenSessionActions.logScreenSession),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectUserId),
        this.store.select(AuthSelectors.selectCurrentUserGroupId),
        this.store.select(AuthSelectors.selectUTCOffset),
        this.store.select(AuthSelectors.selectScreenSession),
        this.store.select(AuthSelectors.selectMpUserRole),
      ]),
      filter(([, , , , , mpUserRole]) => !MINDPLAY_EMPLOYEE_ROLES.includes(mpUserRole)),
      switchMap(([action, userId, userGroupId, utcOffset, screenSession]) => {
        const startTime = DateUtils.new().utcOffset(utcOffset, false); //get the utcOffset fixed date
        const inactivityTimeoutInSeconds = 300; // @TODO: Replace with actual inactivity timeout from system conf.
        const expirationTime = DateUtils.new().add(inactivityTimeoutInSeconds, 's');

        const postData: ScreenSessionResponse = {
          userGroupId: userGroupId,
          screenType: action.screenType,
          startTime: DateUtils.new(startTime).utc(),
          expirationTime: expirationTime.utc(),
        };

        if (screenSession?.screenType === action.screenType) {
          return from(
            this.screenSessionService.updateScreenSession(
              userId,
              screenSession.screenSessionId,
              postData.expirationTime.format()
            )
          ).pipe(
            map(res =>
              ScreenSessionActions.logScreenSessionSuccess({
                data: { ...postData, screenSessionId: res.objectId },
              })
            ),
            catchError(error => of(ScreenSessionActions.logScreenSessionFailure({ error })))
          );
        } else {
          return from(this.screenSessionService.postScreenSession(userId, postData)).pipe(
            map(res =>
              ScreenSessionActions.logScreenSessionSuccess({
                data: { ...postData, screenSessionId: res.objectId },
              })
            ),
            catchError(error => of(ScreenSessionActions.logScreenSessionFailure({ error })))
          );
        }
      })
    );
  });

  checkTokenValidation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CheckTokenActions.checkTokenValidation),
      switchMap(action =>
        this.authService.getUserInfo(action.token).pipe(
          map(data => {
            // Store MP token to perform api async validations
            this.tokenService.setAuthToken({ mpToken: action.token });

            return CheckTokenActions.checkTokenValidationSuccess({ user: data });
          }),
          catchError(error => of(CheckTokenActions.checkTokenValidationFailure({ error })))
        )
      )
    );
  });

  checkEmailConfirmation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CheckUserConfirmationActions.checkUserConfirmation),
      switchMap(action => this.authService.getUserConfirmationStatus(action.token, action.userId)),
      map(data => CheckUserConfirmationActions.checkUserConfirmationSuccess({ data })),
      catchError((error, caught) =>
        merge(caught, of(CheckUserConfirmationActions.checkUserConfirmationFailure({ error })))
      )
    );
  });

  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ResetPasswordActions.resetPassword),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectTokenValidatedUser)),
      switchMap(([action, user]) =>
        this.authService.resetPasswordWithToken(user.userId, action.newPassword, action.token).pipe(
          map(() => ResetPasswordActions.resetPasswordSuccess()),
          catchError(error => of(ResetPasswordActions.resetPasswordFailure({ error })))
        )
      )
    );
  });

  setupAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetupAccountActions.setupAccount),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectTokenValidatedUser)),
      switchMap(([action, user]) =>
        forkJoin([
          this.authService.confirmUserAccountWithToken(user.userId, action.request, action.token),
          this.authService.postEulaAccepted(user.userId, action.token),
        ]).pipe(
          map(() => SetupAccountActions.setupAccountSuccess()),
          catchError(error => of(SetupAccountActions.setupAccountFailure({ error })))
        )
      )
    );
  });

  resendSetupAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetupAccountActions.resendSetupAccount),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectTokenValidatedUser)),
      switchMap(([action]) =>
        this.authService.sendConfirmation(action.email).pipe(
          map(() => SetupAccountActions.resendSetupAccountSuccess()),
          catchError(error => of(SetupAccountActions.resendSetupAccountFailure({ error })))
        )
      )
    );
  });

  resendActivationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResendActivationEmailActions.resendActivationEmail),
      switchMap(action => {
        let res$: Observable<any> = of('');

        if (action.email) {
          res$ = this.authService.resendActivationEmail(action.email);
        }

        if (action.token) {
          res$ = this.authService.resendActivationEmailSelf(action.token);
        }

        return res$.pipe(
          map(() => action.email ?? null)
        );
      }),
      map(data => ResendActivationEmailActions.resendActivationEmailSuccess({ data })),
      catchError((error, caught) =>
        merge(caught, of(ResendActivationEmailActions.resendActivationEmailFailure({ error })))
      )
    )
  );

  setupTrialAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SetupAccountActions.setupTrialAccount),
      switchMap(({ request }) => {
        if ('classId' in request && !isNullOrUndefined(request.classId)) {
          return this.authService.createTeacherByClassActivation(
            request as TrialTeacherByClassActivationBindingModel
          );
        }

        return this.authService.createTeacherBySchoolActivation(
          request as TrialTeacherBySchoolActivationBindingModel
        );
      }),
      map(() => SetupAccountActions.setupTrialAccountSuccess()),
      catchError((error, caught) =>
        merge(caught, of(SetupAccountActions.setupTrialAccountFailure({ error })))
      )
    );
  });

  signupAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SignupAccountActions.signupAccount),
      switchMap(action => {
        if (!isNullOrUndefined(action.classId)) {
          return this.authService.signupTeacherByClass({
            email: action.email,
            classId: action.classId,
            request: action.payload,
          });
        }

        return this.authService.signupTeacherBySchool({
          email: action.email,
          schoolId: action.schoolId,
          request: action.payload,
        });
      }),
      map(() => SignupAccountActions.signupAccountSuccess()),
      catchError((error, caught) =>
        merge(caught, of(SignupAccountActions.signupAccountFailure({ error })))
      )
    );
  });

  loadAPIVersion$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiVersionActions.loadAPIVersion),
      concatLatestFrom(() => [
        this.store.select(AuthSelectors.selectApiVersionLoaded),
        this.store.select(AuthSelectors.selectApiVersion),
      ]),
      switchMap(([, apiVersionLoaded, apiVersion]) => {
        if (apiVersionLoaded) {
          return of(apiVersion);
        }

        return this.environmentService.getApiVersion().pipe(map(res => res.version));
      }),
      map(res => ApiVersionActions.loadAPIVersionSuccess({ data: res })),
      catchError((error, caught) =>
        merge(caught, of(ApiVersionActions.loadAPIVersionFailure({ error })))
      )
    );
  });

  //#region licenses
  loadUserLicenses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LicenseActions.loadLicensesByUserId),
      switchMap(action => {
        const userId: number | null = MathUtils.isValidNumber(action.userId);

        if (!userId) {
          throw new Error('Invalid SIS ID');
        }

        return this.licenseService.getLicenseByUserId(userId, action.licenseTypes).pipe(
          map(data => LicenseActions.loadLicensesByUserIdSuccess({ userId, data })),
          catchError(error => of(LicenseActions.loadLicensesByUserIdFailure({ error })))
        );
      })
    );
  });

  loadLicensesByDistrictId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LicenseActions.loadLicensesByDistrictId),
      switchMap(action => {
        return this.licenseService.getDistrictLicensedProducts(action.districtId).pipe(
          map(data =>
            LicenseActions.loadLicensesByDistrictIdSuccess({ districtId: action.districtId, data })
          ),
          catchError(error => of(LicenseActions.loadLicensesByDistrictIdFailure({ error })))
        );
      })
    );
  });

  loadLicensesBySchoolId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LicenseActions.loadLicensesBySchoolId),
      switchMap(action => {
        return this.licenseService.getSchoolLicensedProducts(action.schoolId).pipe(
          map(data =>
            LicenseActions.loadLicensesBySchoolIdSuccess({ schoolId: action.schoolId, data })
          ),
          catchError(error =>
            of(LicenseActions.loadLicensesBySchoolIdFailure({ schoolId: action.schoolId, error }))
          )
        );
      })
    );
  });
  //#endregion

  loadUsersLicenses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserLicenseActions.loadUsersLicenses),
      concatLatestFrom(() => [this.store.select(AuthSelectors.selectUsersLicensesData)]),
      switchMap(([action, loadedLicenses]) => {
        const usersIdsToLoad = loadedLicenses?.length
          ? action.userIds.filter(
              userId => !loadedLicenses.some(license => license.userId === Number(userId))
            )
          : action.userIds;

        // no users need to be loaded
        if (!usersIdsToLoad.length) {
          return of(UserLicenseActions.loadUsersLicensesSuccess({ data: [] }));
        }

        return this.licenseService.getLicensesByUserIds(usersIdsToLoad, action.licenseTypes).pipe(
          map(data => UserLicenseActions.loadUsersLicensesSuccess({ data })),
          catchError(error => of(UserLicenseActions.loadUsersLicensesFailure({ error })))
        );
      })
    );
  });

  loadDistrictAcademicYears$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDistrictActions.loadDistrictAcademicYears),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectMpCustomerId)),
      switchMap(([_, customerId]) => this.academicYearService.getDistrictAcademicYears(customerId)),
      map(data =>
        UserDistrictActions.loadDistrictAcademicYearsSuccess({
          data: data.map(academicYear => ({
            ...academicYear,
            schoolYearStartDate: DateUtils.getDateString(
              DateUtils.newUTC(academicYear.schoolYearStartDate)
            ),
            schoolYearEndDate: DateUtils.getDateString(
              DateUtils.newUTC(academicYear.schoolYearEndDate)
            ),
          })),
        })
      ),
      catchError((error, caught) =>
        merge(caught, of(UserDistrictActions.loadDistrictAcademicYearsFailure({ error })))
      )
    );
  });

  getUserTypeByRole(role: MindplayUserRole, employeeLoggedInAs: boolean) {
    if (MINDPLAY_EMPLOYEE_ROLES.includes(role)) {
      // This is login as district mode
      if (employeeLoggedInAs) {
        return UserType.Teacher;
      }
      return UserType.MPSuperAdmin;
    } else if (
      role === MindplayUserRole.Teacher ||
      role === MindplayUserRole.Administrator ||
      role === MindplayUserRole.DistrictAdministrator ||
      role === MindplayUserRole.ProgramManager
    ) {
      return UserType.Teacher;
    } else {
      return UserType.Student;
    }
  }

  getAdminTypeByRole(role: MindplayUserRole, employeeLoggedInAdminType: AdminType) {
    if (role === MindplayUserRole.Administrator) {
      return AdminType.SchoolAdmin;
    }
    if (role === MindplayUserRole.DistrictAdministrator) {
      return AdminType.DistrictAdmin;
    }
    if (role === MindplayUserRole.ProgramManager) {
      return AdminType.DistrictAdmin;
    }
    // This is login as district mode
    if (!isNullOrUndefined(employeeLoggedInAdminType)) {
      return employeeLoggedInAdminType;
    }
    return AdminType.Unknown;
  }

  clearCacheAndCheckUpdate(closeTabOnLogout: boolean) {
    const authenticationMethod = SessionStorageUtils.get(AUTHENTICATION_METHOD);
    this.tokenService.clearAuthToken();

    // Close browser tab if user logged in via sso providers like classlink or clever
    if (
      authenticationMethod === 'classLink' ||
      authenticationMethod === 'clever' ||
      closeTabOnLogout
    ) {
      try {
        window.close();
      } catch {
        // Do nothing
      }
    }

    // Check if App update is available and apply update to bring app to latest version
    this.serviceWorkerUpdate.applyUpdate();
  }
}
